import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Stepper, Step, StepLabel,
  Checkbox, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  TextField, InputAdornment, Chip, CircularProgress, Alert, Divider, Paper,
  ToggleButtonGroup, ToggleButton, useTheme
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  PlaylistAdd as BulkIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import internalServiceService from '../../services/internal-service.service';
import ApiService from '../../services/api.service';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';

const typingCenterIdOf = () => localStorage.getItem('typingCenterId')
  || JSON.parse(localStorage.getItem('currentUser') || '{}')?.typingCenterId;

const BulkMappingWizard = ({ open, onClose, onComplete }) => {
  const theme = useTheme();
  const [startWith, setStartWith] = useState('services'); // 'services' or 'clients'
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [services, setServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);

  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [orgNames, setOrgNames] = useState({});
  const [orgTotal, setOrgTotal] = useState(0);
  const [orgPage, setOrgPage] = useState(0);
  const [orgPageSize, setOrgPageSize] = useState(25);
  const [orgSearch, setOrgSearch] = useState('');
  const [orgTerm, setOrgTerm] = useState('');
  const [orgsLoading, setOrgsLoading] = useState(false);

  const [chargeGrid, setChargeGrid] = useState({});
  const [existingKeys, setExistingKeys] = useState(new Set()); // `${svcId}__${orgId}` that already have a mapping
  const [gridLoading, setGridLoading] = useState(false);
  const [result, setResult] = useState(null);

  const steps = startWith === 'services'
    ? ['Select Services', 'Select Clients', 'Set Charges']
    : ['Select Clients', 'Select Services', 'Set Charges'];

  useEffect(() => {
    if (open) {
      resetState();
      loadData();
    }
  }, [open]);

  const resetState = () => {
    setActiveStep(0);
    setError(null);
    setSelectedServices([]);
    setSelectedOrgs([]);
    setOrgNames({});
    setOrgPage(0);
    setOrgSearch('');
    setOrgTerm('');
    setChargeGrid({});
    setResult(null);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      setServices(await internalServiceService.getMappableServices());
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  // Toggle handlers
  const toggleService = (svcId) => {
    setSelectedServices(prev =>
      prev.includes(svcId) ? prev.filter(id => id !== svcId) : [...prev, svcId]
    );
  };

  const toggleAllServices = () => {
    setSelectedServices(prev =>
      prev.length === services.length ? [] : services.map(s => s.id)
    );
  };

  const toggleOrg = (org) => {
    setOrgNames(prev => ({ ...prev, [org.id]: org.name }));
    setSelectedOrgs(prev =>
      prev.includes(org.id) ? prev.filter(id => id !== org.id) : [...prev, org.id]
    );
  };

  const allOnPageSelected = organizations.length > 0 && organizations.every(o => selectedOrgs.includes(o.id));

  const toggleAllOrgs = () => {
    const pageIds = organizations.map(o => o.id);
    setOrgNames(prev => ({ ...prev, ...Object.fromEntries(organizations.map(o => [o.id, o.name])) }));
    setSelectedOrgs(prev => allOnPageSelected
      ? prev.filter(id => !pageIds.includes(id))
      : [...prev, ...pageIds.filter(id => !prev.includes(id))]);
  };

  // Charges grid
  // Build each cell from the org's EXISTING mapping when present, else the service default.
  const initChargeGrid = async () => {
    setGridLoading(true);
    // Load existing per-org mappings (one call per selected org), keyed by `${svcId}__${orgId}`.
    const existing = {};
    try {
      const results = await Promise.all(selectedOrgs.map(orgId =>
        internalServiceService.listMappings({ organizationId: orgId })
          .then(rows => ({ orgId, rows: rows || [] }))
          .catch(() => ({ orgId, rows: [] }))
      ));
      for (const { orgId, rows } of results) {
        for (const r of rows) {
          const sid = r.internal_service_id || r.internalServiceId || r.service_id;
          if (!sid) continue;
          // Keep the ACTUAL stored excl + tax (don't reconstruct via 5%); incl is their sum.
          const excl = Number(r.service_charge ?? r.serviceCharge) || 0;
          const tax = Number(r.tax_on_service_charge ?? r.taxOnServiceCharge) || 0;
          existing[`${sid}__${orgId}`] = {
            serviceChargeIncl: excl + tax,
            serviceCharge: excl,
            tax,
            govtCharge: Number(r.govt_charge ?? r.govtCharge) || 0
          };
        }
      }
    } catch (e) { /* fall back to defaults */ }

    const grid = {};
    const exKeys = new Set();
    for (const svcId of selectedServices) {
      const svc = services.find(s => s.id === svcId);
      if (!svc) continue;
      for (const orgId of selectedOrgs) {
        const key = `${svcId}__${orgId}`;
        if (existing[key]) {
          grid[key] = { ...existing[key] };
          exKeys.add(key);
        } else {
          const excl = Number(svc.serviceCharge) || 0;
          const tax = Number(svc.taxOnServiceCharge) || 0;
          grid[key] = { serviceChargeIncl: excl + tax, serviceCharge: excl, tax, govtCharge: Number(svc.govtCharge) || 0 };
        }
      }
    }
    setChargeGrid(grid);
    setExistingKeys(exKeys);
    setGridLoading(false);
  };

  // Reset all cells to the service's default charges (ignores existing mappings). Indicators stay,
  // since a mapping still exists in the DB until saved over.
  const resetToDefaults = () => {
    const grid = {};
    for (const svcId of selectedServices) {
      const svc = services.find(s => s.id === svcId);
      if (!svc) continue;
      for (const orgId of selectedOrgs) {
        const excl = Number(svc.serviceCharge) || 0;
        const tax = Number(svc.taxOnServiceCharge) || 0;
        grid[`${svcId}__${orgId}`] = { serviceChargeIncl: excl + tax, serviceCharge: excl, tax, govtCharge: Number(svc.govtCharge) || 0 };
      }
    }
    setChargeGrid(grid);
  };

  const handleChargeChange = (key, field, value) => {
    const num = value === '' ? '' : (parseFloat(value) || 0);
    setChargeGrid(prev => {
      const cell = { ...prev[key], [field]: num };
      // Editing the incl-tax charge re-splits into excl + 5% VAT (matches single Create Mapping).
      if (field === 'serviceChargeIncl') {
        const incl = Number(num) || 0;
        const excl = Math.round((incl / 1.05) * 100) / 100;
        cell.serviceCharge = excl;
        cell.tax = Math.round((incl - excl) * 100) / 100;
      }
      return { ...prev, [key]: cell };
    });
  };

  const handleNext = () => {
    if (activeStep === 1) initChargeGrid();
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => setActiveStep(prev => prev - 1);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const mappings = [];
      for (const svcId of selectedServices) {
        for (const orgId of selectedOrgs) {
          const key = `${svcId}__${orgId}`;
          const charges = chargeGrid[key] || {};
          // Send the cell's actual excl + tax (kept in sync: stored for existing, re-split on incl edit).
          mappings.push({
            serviceId: svcId,
            organizationId: orgId,
            serviceCharge: Number(charges.serviceCharge) || 0,
            taxOnServiceCharge: Number(charges.tax) || 0,
            govtCharge: Number(charges.govtCharge) || 0
          });
        }
      }
      const res = await internalServiceService.bulkMappings(mappings);
      setResult(res);
      onComplete(res);
    } catch (err) {
      console.error('Bulk mapping failed:', err);
      setError(err?.response?.data?.message || 'Failed to save mappings');
    } finally {
      setSaving(false);
    }
  };

  const canProceed = () => {
    if (activeStep === 0) {
      return startWith === 'services' ? selectedServices.length > 0 : selectedOrgs.length > 0;
    }
    if (activeStep === 1) {
      return startWith === 'services' ? selectedOrgs.length > 0 : selectedServices.length > 0;
    }
    return true;
  };

  const totalMappings = selectedServices.length * selectedOrgs.length;

  // What to show at step 0 and step 1 depends on startWith
  const isServicesStep = (startWith === 'services' && activeStep === 0)
    || (startWith === 'clients' && activeStep === 1);
  const isClientsStep = (startWith === 'clients' && activeStep === 0)
    || (startWith === 'services' && activeStep === 1);

  useEffect(() => {
    const t = setTimeout(() => { setOrgTerm(orgSearch.trim()); setOrgPage(0); }, 300);
    return () => clearTimeout(t);
  }, [orgSearch]);

  useEffect(() => {
    if (!open || !isClientsStep) return undefined;
    let cancelled = false;
    setOrgsLoading(true);
    ApiService.getInternalOrganizationsPage({
      typingCenterId: typingCenterIdOf(),
      type: 'CLIENT',
      ...(orgTerm ? { search: orgTerm } : {}),
      page: orgPage,
      size: orgPageSize
    })
      .then(resp => {
        if (cancelled) return;
        const data = resp?.data?.data;
        setOrganizations(Array.isArray(data?.content) ? data.content : []);
        setOrgTotal(data?.total_elements ?? data?.totalElements ?? 0);
      })
      .catch(() => { if (!cancelled) setError('Failed to load clients'); })
      .finally(() => { if (!cancelled) setOrgsLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isClientsStep, orgTerm, orgPage, orgPageSize]);

  const formatCharge = (svc) => {
    const svcCharge = (Number(svc.serviceCharge) || 0) + (Number(svc.taxOnServiceCharge) || 0);
    const govtCharge = Number(svc.govtCharge) || 0;
    const parts = [];
    if (svcCharge > 0) parts.push(`AED ${svcCharge.toFixed(2)}`);
    if (govtCharge > 0) parts.push(`Govt AED ${govtCharge.toFixed(2)}`);
    if (parts.length === 0) return 'No charge set';
    return `${svc.category || ''} — ${parts.join(' + ')}`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" fontWeight={600}>Bulk Mapping Wizard</Typography>
            <Typography variant="body2" color="text.secondary">
              Map multiple services to multiple clients at once
            </Typography>
          </Box>
          {totalMappings > 0 && (
            <Chip label={`${totalMappings} mappings`} color="primary" size="small" />
          )}
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
        )}

        {/* Start-with toggle — only at step 0 before any selection */}
        {activeStep === 0 && !result && (
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Typography variant="body2" color="text.secondary">Start with:</Typography>
            <ToggleButtonGroup
              exclusive size="small" value={startWith}
              onChange={(e, v) => { if (v) { setStartWith(v); setSelectedServices([]); setSelectedOrgs([]); } }}
              sx={{
                '& .MuiToggleButton-root.Mui-selected': {
                  bgcolor: 'primary.main', color: 'primary.contrastText',
                  '&:hover': { bgcolor: 'primary.dark' }
                }
              }}
            >
              <ToggleButton value="services">Services First</ToggleButton>
              <ToggleButton value="clients">Clients First</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map(label => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
        ) : result ? (
          <Box textAlign="center" py={3}>
            <CheckIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>Mappings Saved</Typography>
            <Box display="flex" justifyContent="center" gap={2} mt={2}>
              <Chip label={`${result.created} created`} color="success" />
              <Chip label={`${result.updated} updated`} color="info" />
              {result.errors?.length > 0 && (
                <Chip label={`${result.errors.length} errors`} color="error" />
              )}
            </Box>
          </Box>
        ) : (
          <>
            {/* Services list */}
            {isServicesStep && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2">
                    Non-uniform services ({services.length} available)
                  </Typography>
                  <Button size="small" onClick={toggleAllServices}>
                    {selectedServices.length === services.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </Box>
                <Paper variant="outlined" sx={{ maxHeight: 350, overflow: 'auto' }}>
                  <List dense>
                    {services.map(svc => (
                      <ListItem key={svc.id} disablePadding>
                        <ListItemButton onClick={() => toggleService(svc.id)} dense>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Checkbox checked={selectedServices.includes(svc.id)} size="small" tabIndex={-1} />
                          </ListItemIcon>
                          <ListItemText
                            primary={svc.name}
                            secondary={formatCharge(svc)}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                    {services.length === 0 && (
                      <ListItem>
                        <ListItemText primary="No non-uniform services found. Set a service to per-org pricing first." />
                      </ListItem>
                    )}
                  </List>
                </Paper>
              </Box>
            )}

            {/* Clients list */}
            {isClientsStep && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} gap={2}>
                  <Typography variant="subtitle2">
                    Clients ({orgTotal} found, {selectedOrgs.length} selected)
                  </Typography>
                  <Button size="small" onClick={toggleAllOrgs} disabled={organizations.length === 0}>
                    {allOnPageSelected ? 'Deselect this page' : 'Select this page'}
                  </Button>
                </Box>
                <TextField
                  size="small" fullWidth placeholder="Search clients..." value={orgSearch}
                  onChange={e => setOrgSearch(e.target.value)} sx={{ mb: 1 }}
                  InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
                />
                <Paper variant="outlined">
                  <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                    {orgsLoading ? (
                      <ListItem><Box display="flex" justifyContent="center" width="100%" py={2}><CircularProgress size={22} /></Box></ListItem>
                    ) : organizations.length === 0 ? (
                      <ListItem><ListItemText primary={orgTerm ? 'No clients match your search.' : 'No clients found.'} /></ListItem>
                    ) : organizations.map(org => (
                      <ListItem key={org.id} disablePadding>
                        <ListItemButton onClick={() => toggleOrg(org)} dense>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Checkbox checked={selectedOrgs.includes(org.id)} size="small" tabIndex={-1} />
                          </ListItemIcon>
                          <ListItemText
                            primary={org.name}
                            secondary={org.email || org.contactPerson || ''}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                  <TablePagination
                    component="div"
                    count={orgTotal}
                    page={orgPage}
                    onPageChange={(_, p) => setOrgPage(p)}
                    rowsPerPage={orgPageSize}
                    onRowsPerPageChange={e => { setOrgPageSize(parseInt(e.target.value, 10)); setOrgPage(0); }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: '0.75rem' } }}
                  />
                </Paper>
              </Box>
            )}

            {/* Charges Grid */}
            {activeStep === 2 && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2">
                    {selectedServices.length} services × {selectedOrgs.length} clients = {totalMappings} mappings
                  </Typography>
                  <Button size="small" onClick={resetToDefaults}>
                    Reset to Service Defaults
                  </Button>
                </Box>
                {gridLoading && (
                  <Box display="flex" justifyContent="center" alignItems="center" gap={1} py={2}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">Loading existing mappings…</Typography>
                  </Box>
                )}
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead sx={getBrandedTableHeadSx(theme)}>
                      <TableRow>
                        <TableCell>Service</TableCell>
                        <TableCell>Client</TableCell>
                        <TableCell align="right">Govt Charge</TableCell>
                        <TableCell align="right">Service Charge (Incl)</TableCell>
                        <TableCell align="right">Service Charge (Excl)</TableCell>
                        <TableCell align="right">Tax (5% VAT)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedServices.map(svcId => {
                        const svc = services.find(s => s.id === svcId);
                        return selectedOrgs.map(orgId => {
                          const orgName = orgNames[orgId];
                          const key = `${svcId}__${orgId}`;
                          const charges = chargeGrid[key] || {};
                          // Show the cell's actual excl + tax (stored for existing, re-split on incl edit).
                          const excl = Number(charges.serviceCharge) || 0;
                          const tax = Number(charges.tax) || 0;
                          return (
                            <TableRow key={key}>
                              <TableCell>
                                <Typography variant="body2" fontWeight={500}>{svc?.name}</Typography>
                              </TableCell>
                              <TableCell>
                                <Box display="flex" alignItems="center" gap={0.75}>
                                  <Typography variant="body2">{orgName}</Typography>
                                  {existingKeys.has(key) && (
                                    <Chip label="Mapped" size="small" color="info" sx={{ height: 18, fontSize: '0.6rem' }} />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <TextField
                                  size="small" type="number"
                                  value={charges.govtCharge ?? ''}
                                  onChange={e => handleChargeChange(key, 'govtCharge', e.target.value)}
                                  onWheel={e => e.target.blur()}
                                  inputProps={{ min: 0, step: 0.5, style: { textAlign: 'right', fontSize: '0.8rem', padding: '4px 8px' } }}
                                  sx={{ width: 110 }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <TextField
                                  size="small" type="number"
                                  value={charges.serviceChargeIncl ?? ''}
                                  onChange={e => handleChargeChange(key, 'serviceChargeIncl', e.target.value)}
                                  onWheel={e => e.target.blur()}
                                  inputProps={{ min: 0, step: 0.5, style: { textAlign: 'right', fontSize: '0.8rem', padding: '4px 8px' } }}
                                  sx={{ width: 110 }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" color="text.secondary">{excl.toFixed(2)}</Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" color="text.secondary">{tax.toFixed(2)}</Typography>
                              </TableCell>
                            </TableRow>
                          );
                        });
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>
          {result ? 'Done' : 'Cancel'}
        </Button>
        {!result && (
          <>
            {activeStep > 0 && <Button onClick={handleBack}>Back</Button>}
            {activeStep < 2 ? (
              <Button variant="contained" onClick={handleNext} disabled={!canProceed()}>
                Next
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || totalMappings === 0}
                startIcon={saving ? <CircularProgress size={16} /> : <BulkIcon />}
              >
                {saving ? 'Saving...' : `Save ${totalMappings} Mappings`}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkMappingWizard;
